/* ============================================================================
   NOVA Pro theme JS — zero deps, native Web Components
   ============================================================================ */

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const currency = document.documentElement.lang?.includes('en') ? '$' : '';
  if (window.Shopify?.currency?.active) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify.currency.active
      }).format(cents / 100);
    } catch (e) { /* fall through */ }
  }
  return currency + amount;
}

function novaConfig() {
  if (window.NovaThemeConfig) return window.NovaThemeConfig;
  const el = document.querySelector('[data-nova-cart-config]');
  try { window.NovaThemeConfig = JSON.parse(el?.textContent || '{}'); }
  catch (e) { window.NovaThemeConfig = {}; }
  return window.NovaThemeConfig;
}

function novaRoute(name, fallback) {
  return novaConfig().routes?.[name] || fallback;
}

function updateCartCount(count) {
  document.querySelectorAll('[data-cart-count]').forEach((el) => {
    el.textContent = count;
    el.classList.remove('is-bumping');
    void el.offsetWidth;
    el.classList.add('is-bumping');
  });
  const live = document.querySelector('[data-cart-live-region]');
  if (live) {
    live.textContent = count === 0 ? 'Cart is empty' : `${count} item${count !== 1 ? 's' : ''} in cart`;
  }
}

// ---------- Reveal on scroll ----------
const revealObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry, i) => {
      if (entry.isIntersecting) {
        entry.target.style.setProperty('--reveal-delay', i % 6);
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  },
  { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
);
function initReveals(root) {
  const scope = root || document;
  const inEditor = window.Shopify && window.Shopify.designMode;
  scope.querySelectorAll('.reveal').forEach((el) => {
    if (inEditor) {
      // In the theme editor, sections are injected after DOMContentLoaded and
      // may never trigger the observer — show them immediately so adding or
      // configuring a section never leaves the preview blank/white.
      el.classList.add('is-visible');
    } else {
      revealObserver.observe(el);
    }
  });
}
document.addEventListener('DOMContentLoaded', () => initReveals());
// Re-run when a section is added or edited in the theme editor.
document.addEventListener('shopify:section:load', (e) => initReveals(e.target));

// ---------- Multicolumn media — mobile-friendly progressive video loading ----------
// Shopify's video_tag prints every source immediately. Keeping preload="none"
// prevents off-screen carousel videos from competing for bandwidth; the observer
// warms metadata shortly before a card is visible and pointer/focus intent raises
// the selected video to streaming priority.
const multicolumnVideoObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        primeMulticolumnVideo(entry.target, 'metadata');
        multicolumnVideoObserver.unobserve(entry.target);
      });
    }, { rootMargin: '320px 50%', threshold: 0.01 })
  : null;

function primeMulticolumnVideo(video, priority = 'metadata') {
  if (!(video instanceof HTMLVideoElement)) return;
  const current = video.dataset.novaPreload || 'none';
  const rank = { none: 0, metadata: 1, auto: 2 };
  if ((rank[current] || 0) >= (rank[priority] || 0)) return;
  video.dataset.novaPreload = priority;
  video.preload = priority;
  if (video.readyState === HTMLMediaElement.HAVE_NOTHING) video.load();
  if (video.autoplay && video.muted) video.play().catch(() => {});
}

function initMulticolumnVideos(root = document) {
  root.querySelectorAll('.multicolumn__video').forEach((video) => {
    if (video.dataset.novaVideoReady === 'true') return;
    video.dataset.novaVideoReady = 'true';
    video.dataset.novaPreload = video.preload || 'none';
    video.setAttribute('playsinline', '');
    multicolumnVideoObserver?.observe(video);
    const prioritize = () => primeMulticolumnVideo(video, 'auto');
    video.addEventListener('pointerenter', prioritize, { once: true, passive: true });
    video.addEventListener('pointerdown', prioritize, { once: true, passive: true });
    video.addEventListener('touchstart', prioritize, { once: true, passive: true });
    video.addEventListener('focus', prioritize, { once: true, passive: true });
  });
}

function initMulticolumnSliders(root = document) {
  root.querySelectorAll('[data-multicolumn-slider]').forEach((slider) => {
    if (slider.dataset.novaSliderReady === 'true') return;
    const track = slider.querySelector('[data-multicolumn-track]');
    const slides = [...slider.querySelectorAll('[data-multicolumn-slide]')];
    const dots = [...slider.querySelectorAll('[data-multicolumn-dot]')];
    const previous = slider.querySelector('[data-multicolumn-prev]');
    const next = slider.querySelector('[data-multicolumn-next]');
    if (!track || slides.length < 2 || !previous || !next) return;
    slider.dataset.novaSliderReady = 'true';
    let activeIndex = 0;
    let frame = 0;

    const updateControls = (index) => {
      activeIndex = Math.max(0, Math.min(slides.length - 1, index));
      previous.disabled = activeIndex === 0;
      next.disabled = activeIndex === slides.length - 1;
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === activeIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    const nearestSlide = () => {
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let closestIndex = 0;
      let closestDistance = Infinity;
      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft - track.offsetLeft + slide.offsetWidth / 2;
        const distance = Math.abs(slideCenter - trackCenter);
        if (distance < closestDistance) { closestDistance = distance; closestIndex = index; }
      });
      updateControls(closestIndex);
    };

    const goTo = (index, behavior = 'smooth') => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: slides[targetIndex].offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : behavior });
      updateControls(targetIndex);
    };

    track.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(nearestSlide);
    }, { passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(activeIndex - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(activeIndex + 1); }
    });
    previous.addEventListener('click', () => goTo(activeIndex - 1));
    next.addEventListener('click', () => goTo(activeIndex + 1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => goTo(index)));
    window.addEventListener('resize', nearestSlide, { passive: true });
    updateControls(0);
  });
}

document.addEventListener('DOMContentLoaded', () => { initMulticolumnVideos(); initMulticolumnSliders(); });
document.addEventListener('shopify:section:load', (e) => { initMulticolumnVideos(e.target); initMulticolumnSliders(e.target); });
document.addEventListener('shopify:block:select', (event) => {
  const slide = event.target.closest?.('[data-multicolumn-slide]');
  const slider = slide?.closest('[data-multicolumn-slider]');
  if (!slide || !slider) return;
  const track = slider.querySelector('[data-multicolumn-track]');
  track?.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
});

// ---------- Sticky header ----------
class ScrollHeader extends HTMLElement {
  connectedCallback() {
    this._onScroll = () => {
      this.dataset.scrolled = window.scrollY > 20 ? 'true' : 'false';
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this._onScroll); }
}
if (!customElements.get('scroll-header')) customElements.define('scroll-header', ScrollHeader);

// ---------- Hero ----------
class HeroBanner extends HTMLElement {
  connectedCallback() { requestAnimationFrame(() => this.classList.add('is-loaded')); }
}
if (!customElements.get('hero-banner')) customElements.define('hero-banner', HeroBanner);

// ---------- Marquee announcement ----------
class MarqueeBar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.header-promo__track, .announcement-bar__track');
    if (!this.track) return;
    if (!this.originals) this.originals = Array.from(this.track.children).filter(item => !item.hasAttribute('data-promo-copy')).map(item => item.cloneNode(true));
    this.rebuild = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.fill());
    };
    this.pauseButton = this.querySelector('[data-promo-pause]');
    this.onPause = () => {
      const paused = this.dataset.paused !== 'true';
      this.dataset.paused = String(paused);
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel);
    };
    this.pauseButton?.addEventListener('click', this.onPause);
    this.observer = new ResizeObserver(this.rebuild);
    this.observer.observe(this);
    document.fonts?.ready.then(() => { if (this.isConnected) this.rebuild(); });
    this.rebuild();
  }
  fill() {
    if (!this.isConnected || !this.originals.length || !this.clientWidth) return;
    const group = document.createElement('div');
    group.className = 'header-promo__group';
    this.originals.forEach(item => group.append(item.cloneNode(true)));
    this.track.replaceChildren(group);
    const originalWidth = group.getBoundingClientRect().width;
    if (!originalWidth) return;
    const repeats = Math.ceil(this.clientWidth / originalWidth) + 1;
    for (let i = 1; i < repeats; i++) {
      this.originals.forEach(item => {
        const copy = item.cloneNode(true);
        copy.setAttribute('data-promo-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        copy.removeAttribute('data-shopify-editor-block');
        group.append(copy);
      });
    }
    const distance = group.getBoundingClientRect().width;
    const duplicate = group.cloneNode(true);
    duplicate.setAttribute('aria-hidden', 'true');
    duplicate.inert = true;
    duplicate.querySelectorAll('[data-shopify-editor-block]').forEach(item => item.removeAttribute('data-shopify-editor-block'));
    this.track.append(duplicate);
    const seconds = Math.max(12, Number(this.dataset.speed) || 18);
    const velocity = 1200 / seconds;
    this.style.setProperty('--promo-distance', `${distance}px`);
    this.style.setProperty('--promo-speed', `${distance / velocity}s`);
    this.dataset.ready = 'true';
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.pauseButton?.removeEventListener('click', this.onPause);
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('marquee-bar')) customElements.define('marquee-bar', MarqueeBar);

// ---------- Slideshow ----------
class SlideshowHero extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    this.slides = this.querySelectorAll('.slideshow__slide');
    this.dots = this.querySelectorAll('[data-slideshow-go]');
    this.index = 0;
    this.total = this.slides.length;
    if (this.total < 2) return;

    this.querySelector('[data-slideshow-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-slideshow-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) =>
      dot.addEventListener('click', () => this.go(parseInt(dot.dataset.slideshowGo, 10)))
    );

    this._auto = setInterval(() => this.go(this.index + 1), 6000);
    this.addEventListener('mouseenter', () => clearInterval(this._auto));
    this.addEventListener('mouseleave', () => { this._auto = setInterval(() => this.go(this.index + 1), 6000); });
  }
  go(i) {
    this.index = (i + this.total) % this.total;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.dots.forEach((dot, idx) => dot.dataset.active = idx === this.index ? 'true' : 'false');
  }
  disconnectedCallback() { clearInterval(this._auto); }
}
if (!customElements.get('slideshow-hero')) customElements.define('slideshow-hero', SlideshowHero);

// ---------- Content tabs ----------
class ContentTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-tab-id]');
    this.panels = this.querySelectorAll('[data-tab-panel]');
    this.tabs.forEach((tab) =>
      tab.addEventListener('click', () => this.activate(tab.dataset.tabId))
    );
  }
  activate(id) {
    this.tabs.forEach((tab)   => tab.dataset.active = tab.dataset.tabId === id ? 'true' : 'false');
    this.panels.forEach((p)   => p.dataset.active = p.dataset.tabPanel === id ? 'true' : 'false');
  }
}
if (!customElements.get('content-tabs')) customElements.define('content-tabs', ContentTabs);

// ---------- Scroll to top ----------
class ScrollToTop extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    this._onScroll = () => {
      this.dataset.visible = window.scrollY > 600 ? 'true' : 'false';
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });
    this._onScroll();
  }
  disconnectedCallback() { window.removeEventListener('scroll', this._onScroll); }
}
if (!customElements.get('scroll-to-top')) customElements.define('scroll-to-top', ScrollToTop);

// ---------- Quantity input ----------
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.change(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.change(1));
  }
  change(delta) {
    const cur = parseInt(this.input.value, 10) || 0;
    const min = parseInt(this.input.min, 10) || 0;
    this.input.value = Math.max(min, cur + delta);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }
}
if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

// ---------- Product gallery (desktop thumbs + mobile swipe carousel) ----------
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = this.querySelectorAll('[data-gallery-slide]');
    this.images = this.querySelectorAll('[data-gallery-image]');
    this.thumbs = this.querySelectorAll('[data-gallery-thumb]');
    this.dots = this.querySelectorAll('[data-gallery-dot]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.index = 0;
    this.count = this.slides.length || 1;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.show(parseInt(thumb.dataset.index, 10)));
    });
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.show(parseInt(dot.dataset.index, 10)));
    });

    // Desktop prev/next arrows
    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', () => this.show(this.index - 1));
    if (next) next.addEventListener('click', () => this.show(this.index + 1));

    if (this.track) {
      this._onScroll = () => {
        const w = this.track.clientWidth || 1;
        const i = Math.round(this.track.scrollLeft / w);
        if (i !== this.index) this.setActive(i, false);
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      let startX = 0;
      this.track.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < 40) return;
        this.show(this.index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      // Keyboard navigation when the gallery is focused
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.show(this.index + 1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); this.show(this.index - 1); }
      });
    }

    // Zoom mode comes from the section setting (data-image-zoom on <product-form>)
    this.zoomMode = this.closest('[data-image-zoom]')?.dataset.imageZoom || 'both';
    this.dataset.zoom = this.zoomMode;
    this.initLightbox();
    this.initMagnify();
    this.setActive(0, false);
  }

  activeImage() {
    return Array.from(this.images).find((m) => parseInt(m.dataset.index, 10) === this.index);
  }

  // ----- Lightbox / click-to-zoom -----
  initLightbox() {
    this.lightbox = this.querySelector('[data-gallery-lightbox]');
    if (!this.lightbox) return;
    this.lightboxImage = this.lightbox.querySelector('[data-lightbox-image]');
    this.lightboxCounter = this.lightbox.querySelector('[data-lightbox-counter]');
    // Only real images are zoomable (videos/models are skipped)
    this.imageIndices = Array.from(this.images).map((img) => parseInt(img.dataset.index, 10));

    if (this.zoomMode === 'both' || this.zoomMode === 'lightbox') {
      this.images.forEach((img) => {
        img.addEventListener('click', () => this.openLightbox(parseInt(img.dataset.index, 10)));
      });
    }

    const close = this.lightbox.querySelector('[data-lightbox-close]');
    const lbPrev = this.lightbox.querySelector('[data-lightbox-prev]');
    const lbNext = this.lightbox.querySelector('[data-lightbox-next]');
    if (close) close.addEventListener('click', () => this.closeLightbox());
    if (lbPrev) lbPrev.addEventListener('click', () => this.lightboxStep(-1));
    if (lbNext) lbNext.addEventListener('click', () => this.lightboxStep(1));
    // Click on the backdrop (but not the image/controls) closes
    this.lightbox.addEventListener('click', (e) => { if (e.target === this.lightbox) this.closeLightbox(); });

    this.lightbox.addEventListener('cancel', (event) => { event.preventDefault(); this.closeLightbox(); });
    this.lightbox.addEventListener('close', () => {
      this.lightbox.hidden = true;
      document.body.style.overflow = this._lightboxOverflow || '';
      this.track?.focus({ preventScroll: true });
    });
    this._onLightboxKey = (e) => {
      if (!this.lightbox.open) return;
      if (e.key === 'Escape') this.closeLightbox();
      else if (e.key === 'ArrowRight') this.lightboxStep(1);
      else if (e.key === 'ArrowLeft') this.lightboxStep(-1);
    };
    document.addEventListener('keydown', this._onLightboxKey);
  }

  openLightbox(index) {
    if (!this.lightbox) return;
    const img = Array.from(this.images).find((m) => parseInt(m.dataset.index, 10) === index);
    if (!img) return;
    this.lightboxIndex = index;
    this.lightboxImage.src = img.currentSrc || img.src;
    const highResolution = img.dataset.zoomSrc;
    if (highResolution && highResolution !== this.lightboxImage.src) {
      const highImage = new Image();
      highImage.onload = () => {
        if (this.lightbox.open && this.lightboxIndex === index) this.lightboxImage.src = highResolution;
      };
      highImage.src = highResolution;
    }
    this.lightboxImage.alt = img.alt || '';
    if (this.lightboxCounter) {
      const pos = this.imageIndices.indexOf(index) + 1;
      this.lightboxCounter.textContent = `${pos} / ${this.imageIndices.length}`;
    }
    if (!this.lightbox.open) {
      this._lightboxOverflow = document.body.style.overflow;
      const position = {left: window.scrollX, top: window.scrollY, behavior: 'instant'};
      this.lightbox.hidden = false;
      document.body.style.overflow = 'hidden';
      this.lightbox.showModal();
      window.scrollTo(position);
    }
    this.setActive(index, false);
  }

  lightboxStep(dir) {
    const pos = this.imageIndices.indexOf(this.lightboxIndex);
    const nextPos = Math.max(0, Math.min(pos + dir, this.imageIndices.length - 1));
    this.openLightbox(this.imageIndices[nextPos]);
  }

  closeLightbox() {
    if (!this.lightbox) return;
    if (this.lightbox.open) this.lightbox.close();
  }

  // ----- Hover magnifier (desktop) — follows the cursor over the active image -----
  initMagnify() {
    if (this.zoomMode !== 'both' && this.zoomMode !== 'hover') return;
    this.viewport = this.querySelector('.product-page__gallery-viewport');
    if (!this.viewport || !this.images.length) return;
    this.magnifyLens = document.createElement('div');
    this.magnifyLens.className = 'product-page__magnify';
    this.magnifyLens.setAttribute('aria-hidden', 'true');
    this.viewport.appendChild(this.magnifyLens);

    const isDesktop = () => window.matchMedia('(min-width: 990px)').matches;

    this.viewport.addEventListener('mouseenter', () => {
      if (!isDesktop()) return;
      const img = this.activeImage();
      if (!img) return;
      this.magnifyLens.style.backgroundImage = `url("${img.dataset.zoomSrc || img.src}")`;
      this.magnifyLens.dataset.on = 'true';
    });
    this.viewport.addEventListener('mouseleave', () => { this.magnifyLens.dataset.on = 'false'; });
    this.viewport.addEventListener('mousemove', (e) => {
      if (this.magnifyLens.dataset.on !== 'true') return;
      const rect = this.viewport.getBoundingClientRect();
      const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
      this.magnifyLens.style.backgroundPosition = `${x}% ${y}%`;
    });
  }

  show(index) {
    const next = Math.max(0, Math.min(index, this.count - 1));
    this.setActive(next, true);
  }

  setActive(index, scrollTrack) {
    const changed = this.index !== index;
    this.index = index;
    const desktop = window.matchMedia('(min-width: 990px)').matches;
    this.slides.forEach((slide) => {
      const i = parseInt(slide.dataset.index, 10);
      if (desktop) slide.toggleAttribute('hidden', i !== index);
      else slide.removeAttribute('hidden');
    });
    this.images.forEach((img) => {
      const i = parseInt(img.dataset.index, 10);
      if (desktop && i !== index) img.setAttribute('hidden', '');
      else img.removeAttribute('hidden');
    });
    this.thumbs.forEach((thumb) => {
      thumb.dataset.active = parseInt(thumb.dataset.index, 10) === index ? 'true' : 'false';
    });
    this.dots.forEach((dot) => {
      dot.dataset.active = parseInt(dot.dataset.index, 10) === index ? 'true' : 'false';
    });
    const selectedImage = this.activeImage();
    if (selectedImage) selectedImage.loading = 'eager';
    if (changed && desktop && selectedImage && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !document.body.classList.contains('animations-off')) {
      const reveal = () => {
        if (this.index !== index || !selectedImage.isConnected) return;
        selectedImage.getAnimations().forEach(animation => animation.cancel());
        selectedImage.animate([{ opacity: .35, transform: 'scale(.985)' }, { opacity: 1, transform: 'scale(1)' }], { duration: 360, easing: 'cubic-bezier(.2,.7,.2,1)' });
      };
      if (selectedImage.complete) reveal();
      else selectedImage.addEventListener('load', reveal, { once: true });
    }
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.count}`;
    if (this.magnifyLens) {
      const act = this.activeImage();
      if (act) this.magnifyLens.style.backgroundImage = `url("${act.dataset.zoomSrc || act.src}")`;
      else this.magnifyLens.dataset.on = 'false';
    }
    if (scrollTrack && this.track) {
      const slide = this.slides[index];
      if (slide) this.track.scrollTo({ left: slide.offsetLeft, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
  }

  showBySrc(src) {
    if (!src) return false;
    const filename = src.split('/').pop().split('?')[0];
    const img = Array.from(this.images).find((item) => item.src.includes(filename));
    if (!img) return false;
    this.show(parseInt(img.dataset.index, 10));
    return true;
  }

  showByMediaId(id) {
    if (!id) return false;
    const slide = Array.from(this.slides).find((s) => s.dataset.mediaId === String(id));
    if (!slide) return false;
    this.show(parseInt(slide.dataset.index, 10));
    return true;
  }

  // Last-resort match: find an image whose alt text or filename contains the
  // selected option value (e.g. "Dusty Blue" → alt "dusty blue" / blue-2.jpg).
  showByAlt(values) {
    if (!values || !values.length) return false;
    const imgs = Array.from(this.images);
    for (const raw of values) {
      const v = String(raw).trim().toLowerCase();
      if (v.length < 3) continue;
      const compact = v.replace(/\s+/g, '-');
      const img = imgs.find((im) =>
        (im.alt || '').toLowerCase().includes(v) ||
        im.src.toLowerCase().includes(compact) ||
        im.src.toLowerCase().includes(v.replace(/\s+/g, ''))
      );
      if (img) { this.show(parseInt(img.dataset.index, 10)); return true; }
    }
    return false;
  }
}
if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

// ---------- Product form ----------
class ProductForm extends HTMLElement {
  connectedCallback() {
    try { this.product = JSON.parse(this.dataset.productJson || '{}'); }
    catch (e) { this.product = {}; }
    try { this.mediaMap = JSON.parse(this.querySelector('[data-variant-media-map]')?.textContent || '{}'); }
    catch (e) { this.mediaMap = {}; }
    this.form = this.querySelector('form');
    this.variantIdInput = this.querySelector('[data-variant-id-input]');
    this.priceEl = this.querySelector('[data-product-price]');
    this.atcBtn = this.querySelector('[data-add-to-cart]');
    this.atcText = this.querySelector('[data-atc-text]');
    this.sellingPlanInput = this.form?.querySelector('[data-selling-plan-input]');
    this._plan = null;              // selected selling plan adjustment
    this._atcPriceOverride = null;  // quantity-break tier total (cents)

    this.querySelectorAll('input[name^="options"]').forEach((input) => {
      input.addEventListener('change', () => this.onOptionsChange());
    });
    this.querySelectorAll('[data-selling-plan]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        if (this.sellingPlanInput) this.sellingPlanInput.value = input.dataset.sellingPlan || '';
        this._plan = input.dataset.adjustmentType
          ? { type: input.dataset.adjustmentType, value: parseFloat(input.dataset.adjustmentValue) || 0 }
          : null;
        this.refreshPrice(this.getSelectedVariant());
      });
    });
    this.querySelectorAll('[data-purchase-option]').forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        if (this.sellingPlanInput) this.sellingPlanInput.value = '';
        this._plan = null;
        this.refreshPrice(this.getSelectedVariant());
      });
    });
    this.form?.addEventListener('submit', (e) => this.onSubmit(e));
    this.querySelectorAll('[data-buy-quantity]').forEach(button => {
      button.addEventListener('click', () => {
        if (!this.form || !this.atcBtn || this.atcBtn.disabled) return;
        const quantity = this.form.querySelector('input[name="quantity"]');
        if (!quantity) return;
        quantity.value = button.dataset.buyQuantity;
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
        this.form.requestSubmit(this.atcBtn);
      });
    });
    this.querySelector('[data-bundle-checkout]')?.addEventListener('click', (e) => this.onBundleCheckout(e));

    // Mobile bottom action bar (rendered inside <product-form>, outside the form)
    this.querySelectorAll('[data-mobile-add]').forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); this.onSubmit(e); })
    );
    this.querySelectorAll('[data-shipping-insurance]').forEach((item) => {
      try {
        if (sessionStorage.getItem('nova-protection-opt-out') === 'true') {
          const toggle = item.querySelector('[data-upsell-toggle]');
          if (toggle) toggle.checked = false;
          item.dataset.active = 'false';
        }
      } catch (error) {}
    });
    this.onOptionsChange();
    this.syncAcceleratedCheckout();
  }

  async syncShippingProtectionChoice(item, checked) {
    await window.NovaCart.setProtectionChoice(checked);
  }

  syncAcceleratedCheckout() {
    const hasSelectedExtras = !!this.querySelector('[data-extra-item][data-active="true"]');
    const nativeCheckout = this.querySelector('.product-page__dynamic-checkout');
    const bundleCheckout = this.querySelector('[data-bundle-checkout]');
    const giftConfig = window.NovaCart?.readConfig() || novaConfig();
    const giftEnabled = giftConfig.giftVariantId && (giftConfig.giftEnabled || giftConfig.giftThreshold > 0);
    const needsManagedCheckout = this.querySelector('nova-mix-match')?.count > 1 || hasSelectedExtras || giftEnabled || this._protectionPending || this._protectionError;
    if (nativeCheckout) nativeCheckout.hidden = !!needsManagedCheckout;
    if (bundleCheckout) {
      if (!bundleCheckout.dataset.protectedLabel) bundleCheckout.dataset.protectedLabel = bundleCheckout.textContent.trim();
      const protection = this.querySelector('[data-shipping-insurance][data-active="true"]');
      bundleCheckout.textContent = protection ? bundleCheckout.dataset.protectedLabel : (bundleCheckout.dataset.checkoutLabel || 'Buy now');
      bundleCheckout.hidden = !needsManagedCheckout;
      bundleCheckout.disabled = !!this._protectionPending;
    }
  }

  // ----- variant resolution -----
  selectedOptionValues() {
    const selected = [];
    this.querySelectorAll('input[name^="options"]:checked').forEach((i) => selected.push(i.value));
    return selected;
  }
  getSelectedVariant() {
    const selected = this.selectedOptionValues();
    if (!selected.length) {
      return (this.product.variants || []).find((v) => String(v.id) === this.variantIdInput?.value)
        || (this.product.variants || [])[0];
    }
    return (this.product.variants || []).find((v) => v.options.every((opt, i) => opt === selected[i]));
  }

  // Select a variant by id (sticky-bar dropdown): check the matching option
  // radios, or fall back to writing the hidden id input directly.
  selectVariantById(id) {
    const variant = (this.product.variants || []).find((v) => String(v.id) === String(id));
    if (!variant) return;
    const inputs = this.querySelectorAll('input[name^="options"]');
    if (inputs.length) {
      inputs.forEach((input) => {
        const m = input.name.match(/options\[(.+?)\]/);
        if (!m) return;
        const idx = (this.product.options || []).indexOf(m[1]);
        if (idx < 0) return;
        input.checked = input.value === variant.options[idx];
      });
      this.onOptionsChange();
    } else if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
  }

  // ----- pricing -----
  planAdjustedPrice(cents) {
    if (!this._plan) return cents;
    const { type, value } = this._plan;
    if (type === 'percentage') return Math.round(cents * (100 - value) / 100);
    if (type === 'fixed_amount') return Math.max(0, cents - value);
    if (type === 'price') return value;
    return cents;
  }

  refreshPrice(variant) {
    if (!variant) return;
    const shown = this.planAdjustedPrice(variant.price);
    if (this.priceEl) this.priceEl.textContent = formatMoney(shown);
    const compareEl = this.querySelector('[data-compare-price]');
    const badgeEl = this.querySelector('[data-discount-badge]');
    const compare = Math.max(variant.compare_at_price || 0, variant.price);
    if (compareEl) {
      if (compare > shown) {
        compareEl.textContent = formatMoney(compare);
        compareEl.hidden = false;
        if (badgeEl) {
          badgeEl.textContent = '\u2212' + Math.round((1 - shown / compare) * 100) + '%';
          badgeEl.hidden = false;
        }
      } else {
        compareEl.hidden = true;
        if (badgeEl) badgeEl.hidden = true;
      }
    }
    this.updateAtcLabel(variant);
  }

  updateAtcLabel(variant) {
    const available = !!variant?.available;
    if (this.atcBtn) this.atcBtn.disabled = !available;
    this.querySelectorAll('[data-buy-quantity]').forEach(button => { button.disabled = !available; });
    if (!this.atcText) return;
    let label;
    if (!variant) label = this.atcText.dataset.labelUnavailable || 'Unavailable';
    else if (!available) label = this.atcText.dataset.labelSold || 'Sold out';
    else label = this.atcText.dataset.labelAdd || 'Add to cart';
    const wantsPrice = this.atcText.dataset.displayPrice === 'true';
    if (available && wantsPrice) {
      const cents = this._atcPriceOverride ?? this.planAdjustedPrice(variant.price);
      label += ' \u00b7 ' + formatMoney(cents);
    }
    this.atcText.textContent = label;
  }

  // Called by <quantity-breaks> so the ATC button shows the tier total.
  setAtcPrice(cents) {
    this._atcPriceOverride = Number.isFinite(cents) ? cents : null;
    this.updateAtcLabel(this.getSelectedVariant());
  }

  // Grey out option values with no available variant. A value's availability
  // is judged with all *earlier* options fixed to the current selection
  // (same convention as Shopify's value.available).
  updateOptionAvailability() {
    const selected = this.selectedOptionValues();
    const variants = this.product.variants || [];
    if (!selected.length || !variants.length) return;
    this.querySelectorAll('input[name^="options"]').forEach((input) => {
      const m = input.name.match(/options\[(.+?)\]/);
      if (!m) return;
      const optIndex = (this.product.options || []).indexOf(m[1]);
      if (optIndex < 0) return;
      const matching = variants.filter((v) =>
        v.options[optIndex] === input.value &&
        v.options.every((opt, i) => i >= optIndex || opt === selected[i])
      );
      const available = matching.some((v) => v.available);
      input.disabled = !available;
      const label = input.closest('label');
      if (label) {
        label.classList.toggle('is-disabled', !available);
        label.classList.toggle('is-nonexistent', matching.length === 0);
      }
    });
  }

  updateInventory(el, variant) {
    // Per-variant quantities come from a Liquid-rendered map (product JSON no
    // longer exposes inventory_quantity). null = inventory not tracked.
    if (!el._invMap) {
      try { el._invMap = JSON.parse(el.dataset.inventoryQuantities || '{}'); }
      catch (e) { el._invMap = {}; }
    }
    const qty = variant ? el._invMap[String(variant.id)] : undefined;
    const threshold = parseInt(el.dataset.threshold, 10) || 0;
    let state = 'in';
    let text = el.dataset.inStockText || 'In stock';
    if (!variant || !variant.available) {
      state = 'out';
      text = el.dataset.outOfStockText || 'Out of stock';
    } else if (typeof qty === 'number' && qty > 0 && threshold > 0 && qty <= threshold) {
      state = 'low';
      text = (el.dataset.lowStockText || 'Only [count] left').replace('[count]', qty);
    }
    el.dataset.state = state;
    const textEl = el.querySelector('[data-inventory-text]');
    if (textEl) textEl.textContent = text;
  }

  onOptionsChange() {
    const variant = this.getSelectedVariant();
    this.updateOptionAvailability();
    this.querySelectorAll('input[name^="options"]:checked').forEach((input) => {
      const m = input.name.match(/options\[(.+?)\]/);
      if (!m) return;
      const label = this.querySelector(`[data-option-selected-value="${m[1]}"]`);
      if (label) label.textContent = input.value;
      if (m[1].toLowerCase().includes('size')) {
        this.querySelectorAll('[data-fit-selected-size]').forEach((size) => {
          size.textContent = input.value;
        });
      }
    });

    if (!variant) {
      // combination doesn't exist
      this.updateAtcLabel(null);
      document.querySelectorAll('[data-mobile-add], [data-sticky-atc-trigger]')
        .forEach((btn) => { btn.disabled = true; });
      return;
    }

    this.variantIdInput.value = variant.id;
    if (this.hasAttribute('data-update-url') && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    this.refreshPrice(variant);

    // Subscription plan price hints follow the variant price
    this.querySelectorAll('[data-selling-plan]').forEach((input) => {
      const priceEl = input.closest('label')?.querySelector('[data-plan-price]');
      if (!priceEl) return;
      const type = input.dataset.adjustmentType;
      const value = parseFloat(input.dataset.adjustmentValue) || 0;
      let cents = variant.price;
      if (type === 'percentage') cents = Math.round(cents * (100 - value) / 100);
      else if (type === 'fixed_amount') cents = Math.max(0, cents - value);
      else if (type === 'price') cents = value;
      priceEl.textContent = formatMoney(cents);
    });

    // SKU
    document.querySelectorAll('[data-product-sku]').forEach((el) => {
      const valEl = el.querySelector('[data-sku-value]');
      if (valEl) valEl.textContent = variant.sku || '';
      el.hidden = !variant.sku;
    });

    // Inventory status
    this.querySelectorAll('[data-inventory]').forEach((el) => this.updateInventory(el, variant));

    // Media — exact variant first, then another size of the same colour, then
    // colour text in the image alt/filename. This keeps colour switching useful
    // even when a merchant only assigned one image per colour instead of one
    // image to every colour/size combination.
    const gallery = this.querySelector('product-gallery');
    if (gallery) {
      const mediaId = this.mediaMap[String(variant.id)];
      let jumped = !!(mediaId && gallery.showByMediaId?.(mediaId));
      if (!jumped) {
        jumped = !!gallery.showBySrc?.(variant.featured_image?.src);
      }
      const optionNames = this.product.options || [];
      const colorIndex = optionNames.findIndex((name) => /colou?r/i.test(name));
      const selectedValues = this.selectedOptionValues();
      if (!jumped && colorIndex >= 0 && selectedValues[colorIndex]) {
        const colorValue = selectedValues[colorIndex];
        const sibling = (this.product.variants || []).find((candidate) =>
          candidate.options?.[colorIndex] === colorValue && candidate.featured_image?.src
        );
        jumped = !!gallery.showBySrc?.(sibling?.featured_image?.src);
        if (!jumped) gallery.showByAlt?.([colorValue]);
      } else if (!jumped) {
        gallery.showByAlt?.(selectedValues);
      }
    }

    // Sticky bars
    document.querySelectorAll('[data-sticky-price]').forEach((el) => {
      el.textContent = formatMoney(this.planAdjustedPrice(variant.price));
    });
    document.querySelectorAll('[data-sticky-image]').forEach((img) => {
      const src = variant.featured_image?.src;
      if (src) img.src = src + (src.includes('?') ? '&' : '?') + 'width=96';
    });
    document.querySelectorAll('[data-sticky-variant-select]').forEach((sel) => {
      sel.value = String(variant.id);
    });
    document.querySelectorAll('[data-sticky-atc-trigger]').forEach((btn) => {
      btn.disabled = !variant.available;
      btn.textContent = variant.available
        ? (btn.dataset.labelAdd || 'Add to cart')
        : (btn.dataset.labelSold || 'Sold out');
    });
    document.querySelectorAll('[data-mobile-add]').forEach((btn) => {
      btn.disabled = !variant.available;
    });

    this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
  }

  // ----- add to cart -----
  // Checked upsells / unlocked gifts ride along with the main item.
  getExtraItems() {
    const items = [];
    this.querySelectorAll('[data-extra-item][data-active="true"]').forEach((el) => {
      const id = parseInt(el.dataset.variantId, 10);
      if (!id) return;
      const item = { id, quantity: parseInt(el.dataset.quantity, 10) || 1 };
      if (el.hasAttribute('data-gift')) item.properties = { _gift: 'true' };
      items.push(item);
    });
    return items;
  }

  validate() {
    if (this.form && !this.form.checkValidity()) {
      this.form.reportValidity();
      return false;
    }
    return true;
  }

  buildAddPayload(extras = this.getExtraItems()) {
    const formData = new FormData(this.form);
    const mixItems = this.querySelector('nova-mix-match')?.getItems();
    if (!mixItems && !extras.length) return { body: formData, headers: { Accept: 'application/javascript' }, hasExtras: false, hasRequiredExtras: false };
    const hasRequiredExtras = !!this.querySelector('[data-extra-item][data-active="true"][data-required-extra]');
    const main = {
      id: parseInt(formData.get('id'), 10),
      quantity: parseInt(formData.get('quantity'), 10) || 1
    };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) main.selling_plan = sellingPlan;
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const m = key.match(/^properties\[(.+)\]$/);
      if (m && value) properties[m[1]] = value;
    }
    if (Object.keys(properties).length) main.properties = properties;
    return {
      body: JSON.stringify({ items: mixItems ? [...mixItems.map((item, index) => index === 0 && main.properties ? {...item, properties: main.properties} : item), ...extras] : [main, ...extras] }),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      hasExtras: true,
      hasRequiredExtras: hasRequiredExtras || !!mixItems
    };
  }

  // Optional extras may fall back to the main item if they become unavailable.
  // Required selected extras (shipping protection) must never be silently lost.
  async addToCart() {
    const previous = window.novaProtectionAddQueue || Promise.resolve();
    const request = previous.catch(() => {}).then(() => this.performCartAdd());
    window.novaProtectionAddQueue = request;
    return request;
  }

  async performCartAdd() {
    await this._protectionSync;
    let extras = this.getExtraItems();
    const protection = this.querySelector('[data-shipping-insurance]');
    if (protection) {
      const response = await fetch(novaRoute('cart', '/cart') + '.js');
      if (!response.ok) throw new Error('Could not verify shipping protection');
      const cart = await response.json();
      const matching = cart.items.filter((line) => String(line.variant_id) === protection.dataset.variantId);
      if (matching.length) {
        const selected = protection.dataset.active === 'true';
        const updates = Object.fromEntries(matching.map((line, index) => [line.key, selected && index === 0 ? 1 : 0]));
        const result = await fetch(novaRoute('cartUpdate', '/cart/update.js'), {
          method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({updates})
        });
        if (!result.ok) throw new Error('Could not update shipping protection');
        extras = extras.filter((item) => String(item.id) !== protection.dataset.variantId);
      }
    }
    const { body, headers, hasExtras, hasRequiredExtras } = this.buildAddPayload(extras);
    let res = await fetch(novaRoute('cartAdd', '/cart/add.js'), { method: 'POST', body, headers });
    if (!res.ok && hasExtras && !hasRequiredExtras) {
      res = await fetch(novaRoute('cartAdd', '/cart/add.js'), {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'application/javascript' }
      });
    }
    if (res.ok) await window.NovaCart?.checkGift();
    return res;
  }

  async onBundleCheckout(e) {
    e.preventDefault();
    if (!this.validate()) return;
    const btn = e.currentTarget;
    const original = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Preparing checkout\u2026';
    try {
      const res = await this.addToCart();
      if (!res.ok) throw new Error('Bundle add failed');
      window.location.href = novaConfig().checkoutUrl || '/checkout';
    } catch (err) {
      btn.disabled = false;
      btn.textContent = original;
    }
  }

  async onSubmit(e) {
    e.preventDefault();
    if (!this.validate()) return;
    const btn = this.atcBtn || e.submitter;
    const text = this.atcText || btn;
    if (btn) btn.disabled = true;
    if (text) text.textContent = 'Adding\u2026';
    try {
      const res = await this.addToCart();
      if (!res.ok) throw new Error('Add to cart failed');
      const cart = await (await fetch(novaRoute('cart', '/cart') + '.js')).json();
      updateCartCount(cart.item_count);
      await window.NovaCart?.checkGift?.(cart);
      const drawer = document.querySelector('cart-drawer');
      if (drawer) { await drawer.refresh(); drawer.open(); }
      else {
        const note = document.querySelector('cart-notification');
        if (note) { note.show?.(); }
      }
      if (text) text.textContent = 'Added';
      setTimeout(() => {
        if (btn) btn.disabled = false;
        this.updateAtcLabel(this.getSelectedVariant());
      }, 1600);
    } catch (err) {
      console.error(err);
      if (text) text.textContent = 'Error';
      if (btn) btn.disabled = false;
      setTimeout(() => this.updateAtcLabel(this.getSelectedVariant()), 1600);
    }
  }
}
if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

// ---------- Cart drawer ----------
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-cart-drawer-body]');
    this._pending = new Map();   // key -> target quantity (last write wins)
    this._flushTimer = null;
    this._busy = false;
    this.querySelectorAll('[data-cart-drawer-close]').forEach((el) =>
      el.addEventListener('click', () => this.close())
    );
    this.addEventListener('change', (e) => {
      const q = e.target.closest('[data-cart-drawer-quantity]');
      if (q) this.queueUpdate(q.dataset.key, parseInt(q.value, 10));
    });
    this.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-cart-drawer-remove]');
      if (rm) {
        if (String(rm.closest('[data-product-id]')?.dataset.productId) === String(novaConfig().protectionProductId)) window.NovaCart.rememberProtection(false);
        this.queueUpdate(rm.dataset.key, 0);
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
  }
  open()  { this.dataset.open = 'true';  document.body.style.overflow = 'hidden'; }
  close() { this.dataset.open = 'false'; document.body.style.overflow = ''; }
  isOpen(){ return this.dataset.open === 'true'; }

  // Reflect the change in the UI immediately, then debounce the network write so a
  // burst of +/- taps collapses into a single /cart/change.js call.
  queueUpdate(key, quantity) {
    if (!key || Number.isNaN(quantity)) return;
    this.optimisticLine(key, quantity);
    this._pending.set(key, quantity);
    clearTimeout(this._flushTimer);
    this._flushTimer = setTimeout(() => this.flush(), 250);
  }

  // Recompute one line's price + the running totals from the unit price we stored
  // server-side, so the shopper sees the new number instantly (no round-trip wait).
  optimisticLine(key, quantity) {
    const li = this.querySelector(`.cart-drawer__item[data-key="${key}"]`);
    if (!li) return;
    const unit = parseInt(li.dataset.unitPrice, 10);
    const prevLine = parseInt(li.dataset.linePrice, 10) || 0;
    if (Number.isNaN(unit)) return;
    if (quantity <= 0) {
      li.style.opacity = '0.4';
      li.style.pointerEvents = 'none';
      this.bumpTotals(-prevLine);
      li.dataset.linePrice = '0';
      return;
    }
    const newLine = unit * quantity;
    const priceEl = li.querySelector('[data-line-price]');
    if (priceEl) priceEl.textContent = formatMoney(newLine);
    li.dataset.linePrice = newLine;
    this.bumpTotals(newLine - prevLine);
  }

  bumpTotals(deltaCents) {
    this.querySelectorAll('[data-cart-subtotal], [data-checkout-total]').forEach((el) => {
      const next = Math.max(0, (parseInt(el.dataset.cents, 10) || 0) + deltaCents);
      el.dataset.cents = next;
      el.textContent = formatMoney(next);
    });
  }
  setTotals(cents) {
    this.querySelectorAll('[data-cart-subtotal], [data-checkout-total]').forEach((el) => {
      el.dataset.cents = cents;
      el.textContent = formatMoney(cents);
    });
  }

  async flush() {
    if (this._busy) { this._flushTimer = setTimeout(() => this.flush(), 80); return; }
    const updates = [...this._pending.entries()];
    if (!updates.length) return;
    this._pending.clear();
    this._busy = true;
    let cart;
    try {
      for (const [key, quantity] of updates) {
        const res = await fetch(novaRoute('cartChange', '/cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        cart = await res.json();
      }
    } catch (e) {
      this._busy = false;
      await this.refresh();
      return;
    }
    if (cart) {
      updateCartCount(cart.item_count);
      this.setTotals(cart.total_price);
      cart = await window.NovaCart?.checkGift?.(cart) || cart;
      updateCartCount(cart.item_count);
      this.setTotals(cart.total_price);
    }
    this._busy = false;
    await this.refresh();
    if (this._pending.size) {
      clearTimeout(this._flushTimer);
      this._flushTimer = setTimeout(() => this.flush(), 250);
    }
  }

  async refresh() {
    const res = await fetch(`${novaRoute('root', '/')}?sections=cart-drawer`);
    const data = await res.json();
    const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const newBody = doc.querySelector('[data-cart-drawer-body]');
    if (newBody && this.body) this.body.innerHTML = newBody.innerHTML;
    // Re-render the free-shipping bar + upsell in place; leave the countdown
    // timer untouched so it keeps ticking instead of resetting on every change.
    ['.cart-progress', '.cart-upsell'].forEach((sel) => {
      const fresh = doc.querySelector(sel);
      const old = this.querySelector(sel);
      if (fresh && old) old.outerHTML = fresh.outerHTML;
    });
    const newCount = doc.querySelector('.cart-drawer__header h2');
    const oldCount = this.querySelector('.cart-drawer__header h2');
    if (newCount && oldCount) oldCount.innerHTML = newCount.innerHTML;
    this.querySelectorAll('[data-cart-protection-toggle]').forEach(input => { input.checked = window.NovaCart?.protectionSelected() !== false; });
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

// Persist the cart order note (drawer + cart page) on change
document.addEventListener('change', (e) => {
  const note = e.target.closest('[data-cart-note]');
  if (!note) return;
  fetch(novaRoute('cartUpdate', '/cart/update.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ note: note.value })
  });
});

// ---------- Cart toggle ----------
class CartToggle extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      e.preventDefault();
      drawer.open();
    });
  }
}
if (!customElements.get('cart-toggle')) customElements.define('cart-toggle', CartToggle);

// ---------- Cart items (full page) ----------
class CartItems extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', (e) => {
      const input = e.target.closest('[data-cart-quantity]');
      if (input) this.updateLine(input.dataset.key, parseInt(input.value, 10));
    });
    this.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-cart-remove]');
      if (remove) {
        e.preventDefault();
        this.updateLine(remove.dataset.key, 0);
      }
    });
  }
  async updateLine(key, quantity) {
    const row = Array.from(this.querySelectorAll('[data-product-id]')).find(element => element.dataset.key === key);
    if (quantity === 0 && String(row?.dataset.productId) === String(novaConfig().protectionProductId)) window.NovaCart.rememberProtection(false);
    const res = await fetch(novaRoute('cartChange', '/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    let cart = await res.json();
    cart = await window.NovaCart?.checkGift?.(cart) || cart;
    updateCartCount(cart.item_count);
    if (cart.item_count === 0) {
      window.location.reload();
      return;
    }
    await this.refresh(cart);
  }
  async refresh(cart) {
    const itemsId = this.dataset.sectionId;
    if (!itemsId) {
      window.location.reload();
      return;
    }
    const footerEl = document.querySelector('.cart-footer[data-section-id]');
    const footerId = footerEl?.dataset.sectionId;
    const sections = footerId ? `${itemsId},${footerId}` : itemsId;
    const res = await fetch(`${window.location.pathname}?sections=${sections}`);
    const data = await res.json();
    if (data[itemsId]) {
      const doc = new DOMParser().parseFromString(data[itemsId], 'text/html');
      const fresh = doc.querySelector('cart-items');
      if (fresh) this.innerHTML = fresh.innerHTML;
    }
    if (footerId && data[footerId] && footerEl) {
      const doc = new DOMParser().parseFromString(data[footerId], 'text/html');
      const fresh = doc.querySelector('.cart-footer');
      if (fresh) footerEl.innerHTML = fresh.innerHTML;
    } else if (cart && footerEl) {
      const subtotal = footerEl.querySelector('[data-cart-subtotal]');
      if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    }
  }
}
if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

// ---------- Countdown timer ----------
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const end = this.dataset.end;
    const minutes = parseInt(this.dataset.minutes, 10);
    let target;
    if (minutes) target = Date.now() + minutes * 60 * 1000;
    else if (end) target = new Date(end.replace(' ', 'T')).getTime();
    else return;
    this.digits = this.querySelector('[data-countdown-digits]');
    this.tick(target);
    this._timer = setInterval(() => this.tick(target), 1000);
  }
  tick(target) {
    const diff = Math.max(0, target - Date.now());
    const h = String(Math.floor(diff / 3600000)).padStart(2, '0');
    const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
    const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
    if (this.digits) this.digits.textContent = `${h}:${m}:${s}`;
    if (diff <= 0) clearInterval(this._timer);
  }
  disconnectedCallback() { clearInterval(this._timer); }
}
if (!customElements.get('countdown-timer')) customElements.define('countdown-timer', CountdownTimer);

// ---------- Horizontal ticker ----------
class HorizontalTicker extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('.ticker__track');
    if (!track) return;
    track.innerHTML = track.innerHTML + track.innerHTML;
    const speed = parseInt(this.dataset.speed, 10) || 40;
    this.style.setProperty('--ticker-speed', `${speed}s`);
  }
}
if (!customElements.get('horizontal-ticker')) customElements.define('horizontal-ticker', HorizontalTicker);

// ---------- Promo popup ----------
class PromoPopup extends HTMLElement {
  connectedCallback() {
    const key = 'nova_promo_seen';
    if (sessionStorage.getItem(key)) return;
    const delay = (parseInt(this.dataset.delay, 10) || 8) * 1000;
    setTimeout(() => { this.hidden = false; }, delay);
    this.querySelectorAll('[data-promo-close]').forEach((el) =>
      el.addEventListener('click', () => { this.hidden = true; sessionStorage.setItem(key, '1'); })
    );
  }
}
if (!customElements.get('promo-popup')) customElements.define('promo-popup', PromoPopup);

// ---------- Sticky ATC ----------
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('[data-sticky-atc-trigger]');
    const buy = document.querySelector('.product-page__buy');
    const atc = document.querySelector('[data-add-to-cart]');
    const productForm = document.querySelector('product-form form');

    // Variant dropdown in the bar — two-way sync with the main picker
    this.select = this.querySelector('[data-sticky-variant-select]');
    this.select?.addEventListener('change', () => {
      document.querySelector('product-form')?.selectVariantById?.(this.select.value);
    });

    if (buy && 'IntersectionObserver' in window) {
      this._observer = new IntersectionObserver(([entry]) => {
        this.dataset.visible = entry.isIntersecting ? 'false' : 'true';
      }, { threshold: 0, rootMargin: '0px 0px -40px 0px' });
      this._observer.observe(buy);
    } else {
      this._onScroll = () => { this.dataset.visible = window.scrollY > 520 ? 'true' : 'false'; };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
    }

    this.trigger?.addEventListener('click', () => {
      if (atc && !atc.disabled && productForm) productForm.requestSubmit(atc);
      else buy?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }
  disconnectedCallback() {
    this._observer?.disconnect();
    if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
  }
}
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);

// ---------- Shipping checkpoints dynamic dates ----------
class ShippingCheckpoints extends HTMLElement {
  connectedCallback() {
    const fmt = this.dataset.dateFormat || 'mm_dd';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const formatDate = (date) => {
      const d = date.getDate();
      const m = months[date.getMonth()];
      return fmt === 'dd_mm' ? `${d} ${m}` : `${m} ${d}`;
    };
    const addDays = (days) => {
      const d = new Date();
      d.setHours(12, 0, 0, 0);
      d.setDate(d.getDate() + days);
      return d;
    };
    this.querySelectorAll('[data-dynamic-date]').forEach((el) => {
      const template = el.dataset.text || '';
      const min = parseInt(el.dataset.minDays, 10) || 0;
      const max = parseInt(el.dataset.maxDays, 10) || min;
      const start = formatDate(addDays(min));
      const end = formatDate(addDays(max));
      el.innerHTML = template
        .replace(/\[start_date\]/g, `<strong>${start}</strong>`)
        .replace(/\[end_date\]/g, `<strong>${end}</strong>`);
    });
  }
}
if (!customElements.get('shipping-checkpoints')) customElements.define('shipping-checkpoints', ShippingCheckpoints);

// ---------- Quantity breaks ----------
class QuantityBreaks extends HTMLElement {
  connectedCallback() {
    this.pf = this.closest('product-form');
    this.qtyInput = this.pf?.querySelector('form input[name="quantity"]')
      || document.querySelector('#quantity');
    this.showCompare = this.hasAttribute('data-show-compare');
    this.querySelectorAll('input[name="quantity-break"]').forEach((input) => {
      input.addEventListener('change', () => this.apply());
    });
    this.pf?.addEventListener('variant:change', (e) => this.render(e.detail.variant));
    this.apply();
  }

  // Recompute every tier's totals from the live variant price.
  render(variant) {
    if (!variant) return;
    const price = variant.price;
    const compare = Math.max(variant.compare_at_price || 0, price);
    this.querySelectorAll('[data-tier]').forEach((tier) => {
      const qty = parseInt(tier.dataset.qty, 10) || 1;
      const type = tier.dataset.discountType;
      const value = parseFloat(tier.dataset.discountValue) || 0;
      const total = price * qty;
      let discounted = total;
      if (type === 'percent' && value > 0) discounted = Math.round(total * (100 - value) / 100);
      else if (type === 'amount' && value > 0) discounted = Math.max(0, total - Math.round(value * 100));
      const compareTotal = Math.max(compare * qty, discounted);
      const saved = compareTotal - discounted;
      const pct = compareTotal > 0 ? Math.round(saved * 100 / compareTotal) : 0;
      tier.dataset.totalCents = discounted;

      const priceEl = tier.querySelector('[data-tier-price]');
      if (priceEl) priceEl.textContent = formatMoney(discounted);
      const compareEl = tier.querySelector('[data-tier-compare]');
      if (compareEl) {
        const show = this.showCompare && compareTotal > discounted;
        compareEl.hidden = !show;
        if (show) compareEl.textContent = formatMoney(compareTotal);
      }
      tier.querySelectorAll('[data-tier-text]').forEach((el) => {
        const tpl = el.dataset.text || '';
        el.textContent = tpl
          .replace(/\[quantity\]/g, qty)
          .replace(/\[price_each\]/g, formatMoney(Math.round(discounted / qty)))
          .replace(/\[price\]/g, formatMoney(discounted))
          .replace(/\[compare_price\]/g, formatMoney(compareTotal))
          .replace(/\[amount_saved\]/g, formatMoney(saved))
          .replace(/\[percentage\]/g, pct);
      });
    });
    this.apply();
  }

  // Push the selected tier into the quantity input + ATC button price.
  apply() {
    const checked = this.querySelector('input[name="quantity-break"]:checked');
    if (!checked) return;
    if (this.qtyInput && this.qtyInput.value !== checked.dataset.qty) {
      this.qtyInput.value = checked.dataset.qty;
      this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.hasAttribute('data-update-atc-price')) {
      const tier = checked.closest('[data-tier]');
      const total = parseInt(tier?.dataset.totalCents, 10);
      this.pf?.setAtcPrice?.(Number.isFinite(total) ? total : null);
    }
  }
}
if (!customElements.get('quantity-breaks')) customElements.define('quantity-breaks', QuantityBreaks);

// ---------- Cart notification ----------
class CartNotification extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-cart-notification-close]').forEach((el) =>
      el.addEventListener('click', () => this.hide())
    );
  }
  show() {
    this.dataset.visible = 'true';
    clearTimeout(this._hideTimer);
    this._hideTimer = setTimeout(() => this.hide(), 5000);
  }
  hide() { this.dataset.visible = 'false'; }
}
if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);

// ---------- Gift auto-add & quantity gifts ----------
window.NovaCart = {
  config: null,
  queue: Promise.resolve(),
  readConfig() {
    if (this.config) return this.config;
    const element = document.querySelector('[data-nova-cart-config]');
    try { this.config = JSON.parse(element?.textContent || '{}'); } catch (error) { this.config = {}; }
    return this.config;
  },
  protectionSelected() {
    try { return sessionStorage.getItem('nova-protection-opt-out') !== 'true'; } catch (error) { return this.protectionChoice !== false; }
  },
  rememberProtection(checked) {
    this.protectionChoice = checked;
    try { sessionStorage.setItem('nova-protection-opt-out', String(!checked)); } catch (error) {}
    document.querySelectorAll('[data-cart-protection-toggle], [data-shipping-insurance] [data-upsell-toggle]').forEach(input => {
      input.checked = checked;
      const extra = input.closest('[data-shipping-insurance]');
      if (extra) extra.dataset.active = String(checked);
    });
    document.querySelectorAll('product-form').forEach(form => form.syncAcceleratedCheckout?.());
  },
  async setProtectionChoice(checked) {
    this.rememberProtection(checked);
    const cart = await this.checkGift();
    await document.querySelector('cart-drawer')?.refresh();
    const cartItems = document.querySelector('cart-items');
    if (cartItems && cart) await cartItems.refresh(cart);
    this.rememberProtection(checked);
    return cart;
  },
  async init() {
    this.readConfig();
    this.rememberProtection(this.protectionSelected());
    try {
      const cart = await this.checkGift();
      if (cart) {
        await document.querySelector('cart-drawer')?.refresh();
        if (document.querySelector('cart-items')) await document.querySelector('cart-items').refresh(cart);
      }
      document.querySelectorAll('product-form').forEach(form => form.syncAcceleratedCheckout?.());
    } catch (error) { console.error('Gift synchronization failed', error); }
  },
  checkGift() {
    const request = this.queue.catch(() => {}).then(() => this.reconcileGift());
    this.queue = request;
    return request;
  },
  async reconcileGift() {
    const cfg = this.readConfig();
    if (!cfg.giftVariantId && !cfg.protectionVariantId) return null;
    const read = async () => {
      const response = await fetch(novaRoute('cart', '/cart') + '.js');
      if (!response.ok) throw new Error('Could not verify gift eligibility');
      return response.json();
    };
    let cart = await read();
    const isGift = item => String(item.product_id) === String(cfg.giftProductId) || item.properties?._gift === 'true';
    const eligible = cart.items.filter(item => !isGift(item) && String(item.product_id) !== String(cfg.protectionProductId) && item.handle !== 'shipping-insurance');
    if (cfg.protectionVariantId && cfg.protectionAvailable) {
      const lines = cart.items.filter(item => String(item.product_id) === String(cfg.protectionProductId));
      const selected = this.protectionSelected();
      const protectionUpdates = {};
      lines.forEach((item, index) => {
        const quantity = selected && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) protectionUpdates[item.key] = quantity;
      });
      if (Object.keys(protectionUpdates).length) {
        const response = await fetch(novaRoute('cartUpdate', '/cart/update.js'), {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates:protectionUpdates})});
        if (!response.ok) throw new Error('Could not update shipping protection');
        cart = await response.json();
      }
      if (selected && eligible.length && !lines.length) {
        const response = await fetch(novaRoute('cartAdd', '/cart/add.js'), {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:cfg.protectionVariantId,quantity:1}]})});
        if (!response.ok) throw new Error('Could not add shipping protection');
        cart = await read();
      }
    }
    if (!cfg.giftVariantId) { updateCartCount(cart.item_count); return cart; }
    const amount = eligible.reduce((total, item) => total + item.final_line_price, 0);
    const qualified = eligible.some(item => item.quantity > 0) && (cfg.giftEnabled || (cfg.giftThreshold > 0 && amount >= cfg.giftThreshold * 100));
    const allowed = qualified && cfg.giftAvailable && cfg.giftPrice === 0;
    const gifts = cart.items.filter(item => String(item.product_id) === String(cfg.giftProductId));
    const keep = allowed ? gifts.find(item => String(item.variant_id) === String(cfg.giftVariantId)) : null;
    const updates = {};
    for (const item of gifts) {
      const quantity = item === keep ? 1 : 0;
      if (item.quantity !== quantity) updates[item.key] = quantity;
    }
    if (Object.keys(updates).length) {
      const response = await fetch(novaRoute('cartUpdate', '/cart/update.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates })
      });
      if (!response.ok) throw new Error('Could not update gift');
      cart = await response.json();
    }
    if (allowed && !keep) {
      const response = await fetch(novaRoute('cartAdd', '/cart/add.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: cfg.giftVariantId, quantity: 1, properties: { _gift: 'true' } }] })
      });
      if (!response.ok) {
        if (response.status === 422) return cart;
        throw new Error('Could not add gift');
      }
      cart = await read();
    }
    updateCartCount(cart.item_count);
    return cart;
  }
};
document.addEventListener('DOMContentLoaded', () => window.NovaCart.init());
document.addEventListener('change', async event => {
  const toggle = event.target.closest('[data-cart-protection-toggle]');
  if (!toggle) return;
  const container = toggle.closest('[data-cart-protection]');
  const status = container.querySelector('[data-protection-status]');
  toggle.disabled = true;
  container.setAttribute('aria-busy', 'true');
  try {
    await window.NovaCart.setProtectionChoice(toggle.checked);
  } catch (error) {
    if (status) status.textContent = container.dataset.error || 'Could not update protection. Please try again.';
  } finally {
    toggle.disabled = false;
    container.removeAttribute('aria-busy');
  }
});

// ---------- Copy discount code ----------
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-copy-code]');
  if (!btn) return;
  const code = btn.dataset.copyCode;
  if (code && navigator.clipboard) {
    navigator.clipboard.writeText(code);
    const orig = btn.textContent;
    btn.textContent = 'Copied!';
    setTimeout(() => { btn.textContent = orig; }, 1500);
  }
});

// ---------- Product popup dialogs ----------
document.addEventListener('click', (e) => {
  const open = e.target.closest('[data-popup-open]');
  if (open) {
    const dlg = document.getElementById('popup-' + open.dataset.popupOpen);
    dlg?.showModal?.();
    return;
  }
  const close = e.target.closest('[data-popup-close]');
  if (close) close.closest('dialog')?.close?.();
});

// ---------- Scroll-to-section buttons ----------
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-scroll-to]');
  if (!btn) return;
  const name = btn.dataset.scrollTo;
  let target = null;
  try {
    target = document.querySelector('[data-scroll-anchor="' + name + '"]')
      || document.getElementById(name)
      || document.getElementById('shopify-section-' + name)
      || document.querySelector('[data-section="' + name + '"]');
  } catch (err) { /* invalid target name */ }
  target?.scrollIntoView({ behavior: 'smooth' });
});

// ---------- Discount code forms ----------
document.addEventListener('submit', (e) => {
  const form = e.target.closest('[data-discount-form]');
  if (!form) return;
  e.preventDefault();
  const code = form.querySelector('[name="discount"]')?.value?.trim();
  if (!code) return;
  const redirect = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=' + redirect;
});

// ---------- Quick add ----------
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-quick-add]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const variantId = btn.dataset.variantId;
  if (!variantId || btn.classList.contains('is-loading')) return;
  btn.classList.add('is-loading');
  const orig = btn.textContent;
  btn.textContent = btn.dataset.labelAdding || 'Adding…';
  try {
    const res = await fetch(novaRoute('cartAdd', '/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    if (!res.ok) throw new Error('Failed');
    const cart = await (await fetch(novaRoute('cart', '/cart') + '.js')).json();
    updateCartCount(cart.item_count);
    await window.NovaCart?.checkGift?.(cart);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) { await drawer.refresh(); drawer.open(); }
    else document.querySelector('cart-notification')?.show?.();
    btn.textContent = btn.dataset.labelAdded || 'Added';
    setTimeout(() => { btn.textContent = orig; btn.classList.remove('is-loading'); }, 1800);
  } catch (err) {
    btn.textContent = btn.dataset.labelError || 'Error';
    setTimeout(() => { btn.textContent = orig; btn.classList.remove('is-loading'); }, 1600);
  }
});

// ---------- Wishlist (localStorage) ----------
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const id = btn.dataset.productId;
  let list;
  try { list = JSON.parse(localStorage.getItem('nova_wishlist') || '[]'); }
  catch (err) { list = []; }
  if (!Array.isArray(list)) list = [];
  if (list.includes(id)) {
    list = list.filter((x) => x !== id);
    btn.classList.remove('is-active');
  } else {
    list.push(id);
    btn.classList.add('is-active');
  }
  localStorage.setItem('nova_wishlist', JSON.stringify(list));
});
document.addEventListener('DOMContentLoaded', () => {
  let list;
  try { list = JSON.parse(localStorage.getItem('nova_wishlist') || '[]'); }
  catch (err) { list = []; }
  if (!Array.isArray(list)) list = [];
  list.forEach((id) => {
    document.querySelector('[data-wishlist-toggle][data-product-id="' + id + '"]')?.classList.add('is-active');
  });
});

// ---------- Nova timeline ----------
class NovaTimeline extends HTMLElement {
  connectedCallback() {
    if (this.dataset.style === 'horizontal') return;
    this.querySelectorAll('.timeline__item').forEach((item, i) => {
      item.style.transitionDelay = (i * 80) + 'ms';
    });
  }
}
if (!customElements.get('nova-timeline')) customElements.define('nova-timeline', NovaTimeline);

// ---------- Quantity gifts (live unlock, added with the main ATC) ----------
class QuantityGifts extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold, 10) || 0;
    this.pf = this.closest('product-form');
    this.qtyInput = this.pf?.querySelector('form input[name="quantity"]');
    this._onChange = (e) => {
      const t = e.target;
      if (t.name === 'quantity' || t.name === 'quantity-break' || t.closest?.('quantity-input')) {
        this.refresh();
      }
    };
    this.pf?.addEventListener('change', this._onChange);
    this.refresh();
  }
  disconnectedCallback() { this.pf?.removeEventListener('change', this._onChange); }

  refresh() {
    const qty = parseInt(this.qtyInput?.value, 10) || 1;
    const unlocked = this.threshold > 0 && qty >= this.threshold;
    this.dataset.active = unlocked ? 'true' : 'false';
    this.pf?.syncAcceleratedCheckout?.();
    const card = this.querySelector('[data-gift-card]');
    if (card) card.dataset.state = unlocked ? 'unlocked' : 'locked';
    const status = this.querySelector('[data-gift-status]');
    if (status) {
      const tpl = unlocked ? (this.dataset.unlockedText || '') : (this.dataset.lockedText || '');
      status.textContent = tpl
        .replace(/\[threshold\]/g, this.threshold)
        .replace(/\[remaining\]/g, Math.max(0, this.threshold - qty));
    }
  }
}
if (!customElements.get('quantity-gifts')) customElements.define('quantity-gifts', QuantityGifts);

// ---------- PDP upsells (add-with-main-ATC) ----------
document.addEventListener('change', (e) => {
  const toggle = e.target.closest('[data-upsell-toggle]');
  if (toggle) {
    const item = toggle.closest('[data-extra-item]');
    if (item) item.dataset.active = toggle.checked ? 'true' : 'false';
    const productForm = item?.closest('product-form');
    if (item?.hasAttribute('data-shipping-insurance') && productForm) {
      const status = item.querySelector('[data-protection-status]');
      productForm._protectionPending = true;
      productForm._protectionError = false;
      if (status) status.textContent = '';
      productForm._protectionSync = productForm.syncShippingProtectionChoice(item, toggle.checked);
      productForm._protectionSync.then(() => {
        productForm._protectionPending = false;
        productForm.syncAcceleratedCheckout();
      }, () => {
        productForm._protectionPending = false;
        productForm._protectionError = true;
        if (status) status.textContent = 'Unable to update protection. Please toggle the option again before checkout.';
        productForm.syncAcceleratedCheckout();
      });
    }
    productForm?.syncAcceleratedCheckout?.();
    return;
  }
  const sel = e.target.closest('[data-upsell-variant]');
  if (sel) {
    const item = sel.closest('[data-extra-item]');
    if (!item) return;
    item.dataset.variantId = sel.value;
    const opt = sel.selectedOptions[0];
    const price = parseInt(opt?.dataset.price, 10);
    if (!Number.isFinite(price)) return;
    const pct = parseInt(item.dataset.discountPct, 10) || 0;
    const shown = pct > 0 ? Math.round(price * (100 - pct) / 100) : price;
    const priceEl = item.querySelector('[data-upsell-price]');
    if (priceEl) priceEl.textContent = formatMoney(shown);
    const compareEl = item.querySelector('[data-upsell-compare]');
    if (compareEl) {
      const compare = Math.max(parseInt(opt.dataset.comparePrice, 10) || 0, price);
      const show = compare > shown;
      compareEl.hidden = !show;
      compareEl.textContent = show ? formatMoney(compare) : '';
    }
  }
});

// ---------- Share button (Web Share API, clipboard fallback) ----------
class ShareButton extends HTMLElement {
  connectedCallback() {
    this.label = this.querySelector('[data-share-label]');
    this.querySelector('[data-share-trigger]')?.addEventListener('click', async () => {
      const url = this.dataset.shareUrl || window.location.href;
      const title = this.dataset.shareTitle || document.title;
      if (navigator.share) {
        try { await navigator.share({ title, url }); } catch (e) { /* user cancelled */ }
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        if (this.label) {
          const orig = this.label.textContent;
          this.label.textContent = 'Link copied!';
          setTimeout(() => { this.label.textContent = orig; }, 1600);
        }
      }
    });
  }
}
if (!customElements.get('share-button')) customElements.define('share-button', ShareButton);

// ---------- Product recommendations (lazy fetch of this section) ----------
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute('data-performed') || !this.dataset.url) return;
    const load = async () => {
      try {
        const res = await fetch(this.dataset.url);
        if (!res.ok) return;
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        // Only swap when the API actually returned products; otherwise keep the fallback.
        if (fresh?.querySelector('.product-page__related-grid')) {
          this.innerHTML = fresh.innerHTML;
          if (typeof initReveals === 'function') initReveals(this);
        }
      } catch (e) { /* keep fallback */ }
    };
    if ('IntersectionObserver' in window) {
      this._observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        this._observer.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      this._observer.observe(this);
    } else {
      load();
    }
  }
  disconnectedCallback() { this._observer?.disconnect(); }
}
if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);

// ---------- Quick shop (multi-variant cards: pick options in a dialog) ----------
const QuickShop = {
  dialog: null,
  product: null,

  ensureDialog() {
    if (this.dialog) return this.dialog;
    const dlg = document.createElement('dialog');
    dlg.className = 'quick-shop';
    dlg.innerHTML = `
      <button type="button" class="quick-shop__close" data-quick-shop-close aria-label="Close">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
      </button>
      <div class="quick-shop__body" data-quick-shop-body></div>`;
    document.body.appendChild(dlg);
    dlg.addEventListener('click', (e) => {
      const bounds = dlg.getBoundingClientRect();
      const outside = e.target === dlg && (e.clientX < bounds.left || e.clientX > bounds.right || e.clientY < bounds.top || e.clientY > bounds.bottom);
      if (outside || e.target.closest('[data-quick-shop-close]')) dlg.close();
    });
    dlg.addEventListener('close', () => {
      document.body.style.overflow = this.previousOverflow || '';
      this.opener?.focus({ preventScroll: true });
    });
    this.dialog = dlg;
    return dlg;
  },

  async open(handle, url) {
    const dlg = this.ensureDialog();
    const body = dlg.querySelector('[data-quick-shop-body]');
    body.innerHTML = '<p class="quick-shop__loading">Loading…</p>';
    this.opener = document.activeElement;
    this.previousOverflow = document.body.style.overflow;
    const scrollPosition = { left: window.scrollX, top: window.scrollY, behavior: 'instant' };
    document.body.style.overflow = 'hidden';
    dlg.showModal();
    window.scrollTo(scrollPosition);
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) throw new Error('fetch failed');
      this.product = await res.json();
    } catch (e) {
      window.location.href = url || `/products/${handle}`;
      return;
    }
    this.render(body, url);
  },

  render(body, url) {
    const p = this.product;
    const img = p.featured_image ? `<img class="quick-shop__img" src="${p.featured_image}" alt="" width="120" loading="lazy">` : '';
    const options = (p.options || []).map((opt, i) => `
      <label class="quick-shop__option">
        <span>${opt.name}</span>
        <select data-quick-shop-option data-index="${i}">
          ${opt.values.map((v) => `<option value="${String(v).replace(/"/g, '&quot;')}">${v}</option>`).join('')}
        </select>
      </label>`).join('');
    body.innerHTML = `
      <div class="quick-shop__product">
        ${img}
        <div class="quick-shop__meta">
          <h3 class="quick-shop__title">${p.title}</h3>
          <p class="quick-shop__price" data-quick-shop-price></p>
        </div>
      </div>
      ${options}
      <button type="button" class="btn btn--primary quick-shop__atc" data-quick-shop-atc></button>
      <a class="quick-shop__details" href="${url || p.url}">View full details</a>`;

    body.querySelectorAll('[data-quick-shop-option]').forEach((sel) =>
      sel.addEventListener('change', () => this.sync(body))
    );
    body.querySelector('[data-quick-shop-atc]').addEventListener('click', () => this.add(body));
    this.sync(body);
  },

  currentVariant(body) {
    const chosen = [];
    body.querySelectorAll('[data-quick-shop-option]').forEach((sel) => chosen.push(sel.value));
    return (this.product.variants || []).find((v) =>
      v.options.every((opt, i) => opt === chosen[i])
    );
  },

  sync(body) {
    const variant = this.currentVariant(body);
    const priceEl = body.querySelector('[data-quick-shop-price]');
    const atc = body.querySelector('[data-quick-shop-atc]');
    if (!variant) {
      priceEl.textContent = '';
      atc.disabled = true;
      atc.textContent = 'Unavailable';
      return;
    }
    let price = formatMoney(variant.price);
    if (variant.compare_at_price > variant.price) {
      priceEl.innerHTML = `${price} <s>${formatMoney(variant.compare_at_price)}</s>`;
    } else {
      priceEl.textContent = price;
    }
    atc.disabled = !variant.available;
    atc.textContent = variant.available ? `Add to cart · ${price}` : 'Sold out';
    atc.dataset.variantId = variant.id;
  },

  async add(body) {
    const atc = body.querySelector('[data-quick-shop-atc]');
    const variantId = atc.dataset.variantId;
    if (!variantId) return;
    atc.disabled = true;
    const orig = atc.textContent;
    atc.textContent = 'Adding…';
    try {
      const res = await fetch(novaRoute('cartAdd', '/cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 })
      });
      if (!res.ok) throw new Error('add failed');
      const cart = await (await fetch(novaRoute('cart', '/cart') + '.js')).json();
      updateCartCount(cart.item_count);
      await window.NovaCart?.checkGift?.(cart);
      this.dialog.close();
      const drawer = document.querySelector('cart-drawer');
      if (drawer) { await drawer.refresh(); drawer.open(); }
      else document.querySelector('cart-notification')?.show?.();
    } catch (e) {
      atc.textContent = 'Error';
      setTimeout(() => { atc.textContent = orig; atc.disabled = false; }, 1500);
    }
  }
};

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-quick-shop]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  QuickShop.open(btn.dataset.productHandle, btn.dataset.productUrl);
});
